// Чистата логика на складовата карта: периодът по подразбиране, чиповете по вид
// движение с броячи, сумите Приход/Разход, проверката „начално + приход − разход
// = крайно", кои документи се отварят, форматът на кратката дата и редовете за
// Excel (с началното и крайното салдо). Изгледът е в `StockCardMovements*.tsx`.
import dayjs from "dayjs";
import type { StockCard, StockCardEntry, StockMovementType } from "../../../../api/inventoryStockCard";
import { periodOfPreset, type Period } from "../../components/period/periodPresets";
import { shownStock } from "../../components/negativeStock";

// Картата тръгва от последните 30 дни — достатъчно за „какво стана с кафето",
// без да тегли цялата история; по-дълъг период се избира от PeriodPicker.
export const defaultCardPeriod = (): Period => periodOfPreset("last30");

// Подредбата на чиповете — по честота в живия склад, не по азбука
export const CARD_TYPE_ORDER: StockMovementType[] = [
  "delivery",
  "report",
  "writeoff",
  "scrap",
  "stocktake",
  "transfer",
  "substitution",
];

export type CardTypeFilter = "all" | StockMovementType;

export type CardTypeCount = { type: StockMovementType; count: number };

// Чиповете: само видовете, които ги има в периода, с броя им
export const cardTypeCounts = (entries: StockCardEntry[]): CardTypeCount[] => {
  const counts = new Map<StockMovementType, number>();
  for (const entry of entries) counts.set(entry.type, (counts.get(entry.type) ?? 0) + 1);
  return CARD_TYPE_ORDER.filter((type) => counts.has(type)).map((type) => ({
    type,
    count: counts.get(type) ?? 0,
  }));
};

// Филтърът не пипа колоната „Наличност" — тя е истинската наличност след реда,
// а началното/крайното салдо остават над и под редовете.
export const filterCardEntries = (entries: StockCardEntry[], filter: CardTypeFilter) =>
  filter === "all" ? entries : entries.filter((entry) => entry.type === filter);

export const cardTotals = (entries: StockCardEntry[]) => ({
  quantityIn: entries.reduce((sum, entry) => sum + entry.quantityIn, 0),
  quantityOut: entries.reduce((sum, entry) => sum + entry.quantityOut, 0),
});

// Движенията телескопират в ПОКАЗВАНАТА наличност (клампната до 0): начално +
// Σприход − Σразход + Σпродадено без наличност − Σпокрито от минуса = крайно
// (инвентаризацията идва като разлика от max(0, преди) до преброеното).
// Разминаване значи, че BE и FE четат различна история — картата го показва,
// вместо да го крие.
const BALANCE_EPSILON = 0.0005;
export const cardIsConsistent = (card: StockCard) => {
  const totals = cardTotals(card.entries);
  const negativeMoves = card.entries.reduce(
    (sum, entry) => sum + (entry.soldWithoutStock ?? 0) - (entry.coveredNegative ?? 0),
    0,
  );
  const expected = shownStock(card.openingBalance) + totals.quantityIn - totals.quantityOut + negativeMoves;
  return Math.abs(expected - shownStock(card.closingBalance)) < BALANCE_EPSILON;
};

// Знаковата наличност под нулата: колоната „Наличност" показва 0, а редът,
// който я е вкарал под нулата, носи бележка „⚠ 10 кг продадени без наличност".
export const isNegativeBalance = (value: number) => value < -BALANCE_EPSILON;

// Бележката за минуса на реда: разход под нулата (червена; „продадени" при
// разнасяне на отчет и замяната в него, иначе „изписани") или приход, покрил
// минуса от същия ден. Отписаният минус от минали дни не се пише — приходът
// влиза цял и наличността след него е вярна и без бележка.
export type CardNegativeNote = { kind: "sold" | "writtenOff" | "covered"; quantity: number };
export const cardNegativeNote = (entry: StockCardEntry): CardNegativeNote | null => {
  const sold = entry.soldWithoutStock ?? 0;
  if (sold > BALANCE_EPSILON)
    return { kind: entry.documentType === "report" ? "sold" : "writtenOff", quantity: sold };
  const covered = entry.coveredNegative ?? 0;
  if (covered > BALANCE_EPSILON) return { kind: "covered", quantity: covered };
  return null;
};

// Документите, чийто модал може да се отвори от картата. Заварена замяна без
// родител (`substitution`) няма собствен модал.
const OPENABLE_DOCUMENTS: StockMovementType[] = [
  "delivery",
  "report",
  "writeoff",
  "scrap",
  "stocktake",
  "transfer",
];
export const isOpenableDocument = (type: StockMovementType) => OPENABLE_DOCUMENTS.includes(type);

// „05.09" в рамките на една година; при период през нова година — „05.09.25"
export const shortDateFormat = (period: Period) =>
  period.from.year() === period.to.year() ? "DD.MM" : "DD.MM.YY";

export const formatCardDate = (iso: string, format: string) => dayjs(iso).format(format);

// ─── Excel ─────────────────────────────────────────────────────────────────
export type CardExcelRow = {
  date: string;
  document: string;
  quantityIn: number | null;
  quantityOut: number | null;
  balance: number;
  unitPrice: number | null;
  createdBy: string;
  note: string;
};

type ExcelLabels = {
  opening: string;
  closing: string;
  documentOf: (entry: StockCardEntry) => string;
  noteOf: (entry: StockCardEntry) => string; // вкл. бележката за минуса
};

// Редовете са в реда на екрана: началното салдо, движенията (филтрираните),
// крайното салдо — за да се сверява файлът с картата ред по ред.
export const cardExcelRows = (
  card: StockCard,
  entries: StockCardEntry[],
  period: Period,
  labels: ExcelLabels,
): CardExcelRow[] => [
  {
    date: period.from.format("DD.MM.YYYY"),
    document: labels.opening,
    quantityIn: null,
    quantityOut: null,
    balance: shownStock(card.openingBalance),
    unitPrice: null,
    createdBy: "",
    note: "",
  },
  ...entries.map((entry) => ({
    date: dayjs(entry.date).format("DD.MM.YYYY HH:mm"),
    document: labels.documentOf(entry),
    quantityIn: entry.quantityIn || null,
    quantityOut: entry.quantityOut || null,
    balance: shownStock(entry.balanceAfter),
    unitPrice: entry.unitPrice || null,
    createdBy: entry.createdByName ?? "",
    note: labels.noteOf(entry),
  })),
  {
    date: period.to.format("DD.MM.YYYY"),
    document: labels.closing,
    quantityIn: null,
    quantityOut: null,
    balance: shownStock(card.closingBalance),
    unitPrice: null,
    createdBy: "",
    note: "",
  },
];
